import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { useProfiles } from '@/hooks/useInventoryMaster'
import { useStorageConditionMutations, useStorageConditions } from '@/hooks/useStorage'
import { usePermission } from '@/lib/permissions'
import { conditionPatch, type ConditionDraft } from '@/lib/storageConditions'
import { storageTemperaturesHref } from '@/lib/storageRoutes'
import { hasKeptAtOptionFor } from '@/lib/storageTemperatures'
import {
  TEMPERATURE_FAMILIES,
  type StorageConditionMeta,
  type TemperatureFamily,
  type TemperatureFamilyMeta,
} from '@/types/storage'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog'

const EMPTY_NEW: ConditionDraft = { label: '', temperature: '', family: 'FREEZER', persistent: true, active: true }

/**
 * Temperatures tab: the tenant's own list of what a location can be kept at
 * and what a product can require — "Refrigerated 2-8 C", "Freezer -20 C", and
 * any the site adds.
 *
 * Every row is editable in place; Save sends only the rows that changed. The
 * code each temperature carries never changes, so renaming one cannot break a
 * location, a material or a mapping already on it. One in use cannot be
 * deleted — the server says where it is used — but it can be switched off,
 * which stops it being offered while leaving what already uses it working.
 *
 * A unit picks a Kept at option, not a temperature. Some servers create one
 * alongside each new temperature and some do not, so after an add the options
 * are re-read and the message says which happened.
 */
export function TemperaturesTab({ onClose }: { onClose?: () => void }) {
  const { can } = usePermission()
  const canEdit = can('inventory.configure')
  const { data, isLoading, isError, error } = useStorageConditions()
  const { create, update, remove } = useStorageConditionMutations()
  const profiles = useProfiles('storage')

  /** id → the row as edited here; absent means "as saved". */
  const [drafts, setDrafts] = useState<Record<string, ConditionDraft>>({})
  const [adding, setAdding] = useState<ConditionDraft>(EMPTY_NEW)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<StorageConditionMeta | null>(null)
  /** `optionsLink`: the message ends with a link to the Kept at options tab. */
  const [message, setMessage] = useState<{ kind: 'error' | 'ok'; text: string; optionsLink?: boolean } | null>(null)

  const conditions = data?.conditions ?? []
  const families = data?.families ?? []
  const changes = conditions
    .map(c => ({ id: c.id, patch: drafts[c.id] ? conditionPatch(c, drafts[c.id]) : null }))
    .filter((c): c is { id: string; patch: NonNullable<typeof c.patch> } => c.patch !== null)

  function draftOf(c: StorageConditionMeta): ConditionDraft {
    return (
      drafts[c.id] ?? {
        label: c.label,
        temperature: c.temperature,
        family: c.family ?? 'AMBIENT_CRT',
        persistent: c.persistent,
        active: c.active,
      }
    )
  }

  function edit(c: StorageConditionMeta, patch: Partial<ConditionDraft>) {
    setMessage(null)
    setDrafts(prev => ({ ...prev, [c.id]: { ...draftOf(c), ...patch } }))
  }

  async function saveAll() {
    if (changes.some(c => c.patch.label !== undefined && !c.patch.label.trim())) {
      setMessage({ kind: 'error', text: 'Every temperature needs a name.' })
      return
    }
    setSaving(true)
    setMessage(null)
    try {
      // One at a time, so a refusal names the row it was about and the rows
      // before it stay saved.
      for (const change of changes) {
        await update.mutateAsync(change)
        setDrafts(prev => {
          const next = { ...prev }
          delete next[change.id]
          return next
        })
      }
      setMessage({ kind: 'ok', text: 'Temperatures saved' })
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Could not save.' })
    } finally {
      setSaving(false)
    }
  }

  function add() {
    if (!adding.label.trim()) {
      setMessage({ kind: 'error', text: 'Give the new temperature a name, e.g. "Plasma Freezer -30 C".' })
      return
    }
    setMessage(null)
    create.mutate(
      {
        label: adding.label.trim(),
        temperature: adding.temperature.trim(),
        family: adding.family,
        persistent: adding.persistent,
      },
      {
        onSuccess: async c => {
          setAdding(EMPTY_NEW)
          const { data: fresh } = await profiles.refetch()
          setMessage(
            hasKeptAtOptionFor(fresh ?? [], c.name)
              ? { kind: 'ok', text: `Added ${c.label}. It's now available as a Kept at option.` }
              : { kind: 'ok', text: `Added ${c.label}. To use it for a unit, add a Kept at option for it.`, optionsLink: true },
          )
        },
        onError: err => setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Could not add.' }),
      },
    )
  }

  function confirmDelete() {
    const c = deleting
    if (!c) return
    setMessage(null)
    remove.mutate(c.id, {
      onSuccess: () => setMessage({ kind: 'ok', text: `Deleted "${c.label}"` }),
      onError: err => setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Could not delete.' }),
      onSettled: () => setDeleting(null),
    })
  }

  const busy = saving || create.isPending || remove.isPending

  return (
    <div className='flex min-h-0 flex-col gap-3'>
      <p className='text-sm text-muted-foreground'>
        What a location can be kept at and a product can require. Rename, add or switch them off here;
        locations and mappings already on one keep working.
      </p>

      <div className='min-h-0 flex-1 overflow-y-auto rounded-md border'>
        {isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : isError ? (
          <p className='p-4 text-sm text-destructive'>
            Could not load temperatures: {error instanceof Error ? error.message : 'unknown error'}
          </p>
        ) : (
          <Table>
            <TableHeader className='sticky top-0 z-10'>
              <TableRow>
                <TableHead className='w-[28%]'>Name</TableHead>
                <TableHead className='w-[16%]'>Temperature</TableHead>
                <TableHead className='w-[18%]'>Type</TableHead>
                <SwitchHead title='Can be used for storage' hint='Products can rest here, not just pass through' />
                <SwitchHead title='Active' hint='Offered for new choices; existing uses keep working' />
                <TableHead className='w-8' />
              </TableRow>
            </TableHeader>
            <TableBody>
              {conditions.map(c => {
                const d = draftOf(c)
                const dirty = !!drafts[c.id] && conditionPatch(c, d) !== null
                return (
                  <TableRow
                    key={c.id}
                    className={cn('border-l-4 border-l-transparent', dirty && 'border-l-blue-400', !d.active && 'opacity-60')}
                  >
                    <TableCell className='align-top'>
                      <Input
                        value={d.label}
                        disabled={!canEdit || busy}
                        onChange={e => edit(c, { label: e.target.value })}
                        aria-label={`Name of ${c.label}`}
                        className='h-8 text-sm'
                      />
                      <span className='mt-0.5 block font-mono text-[10px] text-muted-foreground'>
                        {c.name}
                        {c.builtIn ? ' · built in' : ''}
                      </span>
                    </TableCell>
                    <TableCell className='align-top'>
                      <Input
                        value={d.temperature}
                        disabled={!canEdit || busy}
                        onChange={e => edit(c, { temperature: e.target.value })}
                        placeholder='e.g. −20 °C'
                        aria-label={`Temperature of ${c.label}`}
                        className='h-8 text-sm'
                      />
                    </TableCell>
                    <TableCell className='align-top'>
                      <FamilySelect
                        value={d.family}
                        families={families}
                        disabled={!canEdit || busy}
                        onChange={family => edit(c, { family })}
                        label={`Type of ${c.label}`}
                      />
                    </TableCell>
                    <TableCell className='align-top pt-3'>
                      <Switch
                        checked={d.persistent}
                        disabled={!canEdit || busy}
                        onCheckedChange={v => edit(c, { persistent: v })}
                        aria-label={`${c.label} can be used for storage`}
                      />
                    </TableCell>
                    <TableCell className='align-top pt-3'>
                      <Switch
                        checked={d.active}
                        disabled={!canEdit || busy}
                        onCheckedChange={v => edit(c, { active: v })}
                        aria-label={`${c.label} is active`}
                      />
                    </TableCell>
                    <TableCell className='align-top pt-2.5'>
                      {canEdit && (
                        <button
                          type='button'
                          className='text-gray-300 hover:text-red-500 disabled:opacity-40'
                          title='Delete temperature'
                          aria-label={`Delete ${c.label}`}
                          disabled={busy}
                          onClick={() => setDeleting(c)}
                        >
                          <Trash2 className='h-4 w-4' />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}

              {canEdit && (
                <TableRow className='bg-gray-50/60 hover:bg-gray-50/60'>
                  <TableCell className='align-top'>
                    <Input
                      value={adding.label}
                      disabled={busy}
                      onChange={e => {
                        setMessage(null)
                        setAdding(a => ({ ...a, label: e.target.value }))
                      }}
                      onKeyDown={e => e.key === 'Enter' && add()}
                      placeholder='New, e.g. Plasma Freezer -30 C'
                      aria-label='Name of the new temperature'
                      className='h-8 bg-white text-sm'
                    />
                  </TableCell>
                  <TableCell className='align-top'>
                    <Input
                      value={adding.temperature}
                      disabled={busy}
                      onChange={e => setAdding(a => ({ ...a, temperature: e.target.value }))}
                      placeholder='e.g. −30 °C'
                      aria-label='Temperature of the new temperature'
                      className='h-8 bg-white text-sm'
                    />
                  </TableCell>
                  <TableCell className='align-top'>
                    <FamilySelect
                      value={adding.family}
                      families={families}
                      disabled={busy}
                      onChange={family => setAdding(a => ({ ...a, family }))}
                      label='Type of the new temperature'
                    />
                  </TableCell>
                  <TableCell className='align-top pt-3'>
                    <Switch
                      checked={adding.persistent}
                      disabled={busy}
                      onCheckedChange={v => setAdding(a => ({ ...a, persistent: v }))}
                      aria-label='The new temperature can be used for storage'
                    />
                  </TableCell>
                  <TableCell colSpan={2} className='align-top'>
                    <Button size='sm' className='h-8 gap-1' disabled={busy} onClick={add}>
                      <Plus className='h-3.5 w-3.5' />
                      {create.isPending ? 'Adding…' : 'Add'}
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {message && (
        <p role='status' className={cn('text-sm', message.kind === 'error' ? 'text-destructive' : 'text-green-700')}>
          {message.text}
          {message.optionsLink && (
            <>
              {' '}
              <Link to={storageTemperaturesHref('options')} className='font-medium text-primary hover:underline'>
                Add a Kept at option
              </Link>
            </>
          )}
        </p>
      )}

      {(onClose || canEdit) && (
        <div className='flex justify-end gap-2'>
          {onClose && (
            <Button variant='outline' onClick={onClose}>
              Close
            </Button>
          )}
          {canEdit && (
            <Button disabled={changes.length === 0 || busy} onClick={saveAll}>
              {saving ? 'Saving…' : `Save changes (${changes.length})`}
            </Button>
          )}
        </div>
      )}

      <ConfirmDeleteDialog
        name={deleting?.label ?? ''}
        open={deleting !== null}
        pending={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      >
        A temperature still in use is refused, and the message says where. Switching it off instead keeps what
        already uses it working.
      </ConfirmDeleteDialog>
    </div>
  )
}

/** A switch column's header with its one-line explanation underneath. */
function SwitchHead({ title, hint }: { title: string; hint: string }) {
  return (
    <TableHead className='h-auto py-2 align-top'>
      <span className='block'>{title}</span>
      <span className='block text-[11px] font-normal normal-case leading-tight tracking-normal opacity-80'>{hint}</span>
    </TableHead>
  )
}

function FamilySelect({
  value,
  families,
  disabled,
  onChange,
  label,
}: {
  value: TemperatureFamily
  families: TemperatureFamilyMeta[]
  disabled: boolean
  onChange: (family: TemperatureFamily) => void
  label: string
}) {
  return (
    <Select value={value} disabled={disabled} onValueChange={v => onChange(v as TemperatureFamily)}>
      <SelectTrigger className='h-8 bg-white text-sm' aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TEMPERATURE_FAMILIES.map(f => (
          <SelectItem key={f} value={f}>
            {families.find(m => m.name === f)?.label ?? f}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
